// SCR-GARAGE-VISIT и MOD-PLAYER-CARD (E06-T08) — ux/03 §12–§13. Публичные блоки ГД 14 §5 из GET /players/{id}:
// шапка, гараж, машина, кооператив, счётчики; без сумки, валют, покупок и почты. «Зайти в гости» — POST visit.
// «Уважить», «В друзья», «Пожаловаться»: эндпоинтов в договоре нет (E10, E08) — кнопки неактивны с подписью (Р-56).
import { useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { ZONES } from '../content/garage.js';
import { num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { online, player } from '../state/game.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ScreenTitle, Sheet, Skeleton, useActionBar } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { respectedToday, useRespect } from './friends.js';

type Profile = ResponseOf<Api['getPlayer']>;

/** Причина отказа визита → текст (Р-51 бэкенда: reason). */
const VISIT_REASON: Record<string, string> = {
  not_neighbor: 'CL-VISIT-NOT-NEIGHBOR', already_today: 'CL-VISIT-ALREADY', hosts_limit: 'CL-VISIT-HOSTS-LIMIT', no_bench: 'CL-VISIT-NO-BENCH', self: 'PROF-VISIT-SELF',
};

function Counters(p: { s: Profile['stats'] }) {
  return (
    <div class="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
      <div class="stat"><span class="small muted">{tr('RANK-RESPECT-MONTH')}</span><b>{num(p.s.respectMonth)}</b></div>
      <div class="stat"><span class="small muted">{tr('CL-VISIT-WEEK-DMG')}</span><b>{num(p.s.bestWeekDamage)}</b></div>
      <div class="stat"><span class="small muted">{tr('CL-VISIT-BOSSES')}</span><b>{num(p.s.bossesDefeated)}</b></div>
    </div>
  );
}

export function GarageVisitScreen(p: ScreenProps) {
  const app = useApp();
  const id = String(p.params['publicId'] ?? player.value?.publicId ?? '');
  const self = id === player.value?.publicId;
  const load = useLoad(() => app.call('getPlayer', { params: { publicId: id } }));
  const [visited, setVisited] = useState<string | null>(null);
  const prof = load.data;
  const respect = useRespect();
  const visit = async () => {
    try {
      const r = await app.call('visitGarage', { params: { publicId: id }, body: {} });
      if (r.bonus) { setVisited(tr('CL-VISIT-DONE')); app.haptic('success'); } else setVisited(tr(VISIT_REASON[r.reason ?? ''] ?? 'CL-VISIT-NOT-NEIGHBOR'));
    } catch (e) { app.handleError(e); }
  };
  useActionBar(self || !prof ? { back: true } : {
    back: true,
    secondary: { text: visited ?? tr('PROF-VISIT'), tone: 'outline', testId: 'visit-go', disabled: !online.value || !!visited, onClick: visit },
    main: (prof.respectedToday || respectedToday.value.has(id))
      ? { text: tr('CL-RESPECT-GIVEN'), testId: 'visit-respect', disabled: true, onClick: () => {} }
      : { text: tr('UI-BTN-RESPECT'), testId: 'visit-respect', disabled: !online.value, onClick: () => respect(id) },
  });
  if (load.error === 'NOT_FOUND') {
    return (
      <>
        <ScreenTitle>{tr('PROF-VISIT-LOCKED')}</ScreenTitle>
        <div class="content screen-bg"><div class="empty" data-testid="visit-locked"><span class="ill">🔒</span>{tr('PROF-VISIT-LOCKED')}</div></div>
      </>
    );
  }
  return (
    <>
      <ScreenTitle right={prof ? <span class="small">{tr('CL-CHAR-LVL', { n: prof.player.level })}</span> : null}>{prof?.player.displayName.slice(0, 18) ?? '…'}</ScreenTitle>
      <div class="content screen-bg store" data-testid="SCR-GARAGE-VISIT">
        {/* Чужой гараж — по арту гаража: карточки в нижней панели, фон виден наполовину (замечание владельца). */}
        <div class="page col store-page">
          <div class="store-panel visit-panel col">
          {self ? <div class="banner">{tr('PROF-VISIT-SELF')}</div> : null}
          {!prof ? <Skeleton h={80} n={3} /> : (
            <>
              <Counters s={prof.stats} />
              <div class="card col" data-testid="visit-zones">
                <b>{tr('CL-VISIT-GARAGE')}</b>
                <div class="visit-zones">
                  {prof.garage.zones.map((z) => (
                    <button key={z.zoneId} class="chip" onClick={() => showToast(tr('UI-GAR-ZONE-LVL', { zone: ZONES[z.zoneId]?.name ?? z.zoneId, lvl: z.level }))}>
                      {ZONES[z.zoneId]?.name ?? z.zoneId} · {z.level}
                    </button>
                  ))}
                </div>
              </div>
              <div class="card col">
                <b>{tr('UI-CAR-TITLE')}</b>
                <span class="small">{prof.car.nodes.map((n) => tr('CL-VISIT-NODE', { node: tr(`CL-VISIT-NODE-${n.nodeId.toUpperCase()}`), n: n.level })).join(' · ')}</span>
              </div>
              {prof.coop ? <div class="card">{prof.coop.name}</div> : null}
              {!self ? (
                <VisitSocial publicId={id} status={prof.friendStatus ?? 'none'} onChange={load.reload} />
              ) : null}
            </>
          )}
          </div>
        </div>
      </div>
    </>
  );
}

/** MOD-PLAYER-CARD (ux/03 §13): лист 40%, «Профиль», «Уважить», «В друзья». */
export function PlayerCardSheet({ entry }: SheetProps) {
  const app = useApp();
  const respectCard = useRespect();
  const id = String(entry.params['publicId']);
  const load = useLoad(() => app.call('getPlayer', { params: { publicId: id } }));
  const prof = load.data;
  return (
    <Sheet entry={entry} title={prof?.player.displayName ?? '…'} actions={[
      { text: tr('UI-NAV-PROFILE'), testId: 'card-profile', onClick: () => { app.nav.closeSheets(); app.nav.push('SCR-GARAGE-VISIT', { publicId: id }); } },
      prof && (prof.respectedToday || respectedToday.value.has(id))
        ? { text: tr('CL-RESPECT-GIVEN'), tone: 'outline' as const, testId: 'card-respect', disabled: true, onClick: () => {} }
        : { text: tr('UI-BTN-RESPECT'), tone: 'outline' as const, testId: 'card-respect', disabled: !online.value || !prof || prof.player.publicId === player.value?.publicId, onClick: () => respectCard(id) },
    ]}>
      {load.error === 'NOT_FOUND' ? <div class="empty">{tr('PROF-VISIT-LOCKED')}</div> : !prof ? <Skeleton h={60} /> : (
        <div class="col" data-testid="MOD-PLAYER-CARD-body">
          <span class="muted">{tr('CL-CHAR-LVL', { n: prof.player.level })}{prof.coop ? ` · ${prof.coop.name}` : ''}</span>
          <Counters s={prof.stats} />
        </div>
      )}
    </Sheet>
  );
}

export function visitError(e: unknown): string | null {
  return e instanceof ApiCallError ? e.code : null;
}

/**
 * «В друзья» по состоянию с сервера (`friendStatus`) и «Пожаловаться» на имя или гараж (E10-T04, E06-T08, ux/10 §3).
 * Жалоба уходит в очередь модерации; повтор в те же сутки сервер не считает второй.
 */
function VisitSocial(p: { publicId: string; status: 'none' | 'outgoing' | 'incoming' | 'friends' | 'self'; onChange: () => void }) {
  const app = useApp();
  const [st, setSt] = useState(p.status);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(false);
  const off = !online.value || busy;
  const friend = async () => {
    setBusy(true);
    try {
      if (st === 'incoming') { await app.call('acceptFriend', { params: { publicId: p.publicId }, body: {} }); setSt('friends'); showToast(tr('CL-FRIENDS-ACCEPTED'), 'success'); }
      else { const r = await app.call('addFriend', { params: { publicId: p.publicId }, body: {} }); setSt(r.status === 'accepted' ? 'friends' : 'outgoing'); showToast(tr(r.status === 'accepted' ? 'CL-FRIENDS-ACCEPTED' : 'CL-VISIT-FRIEND-SENT'), 'success'); }
      app.haptic('success');
      p.onChange();
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code === 'FRIENDS_LIMIT') showToast(tr('CL-FRIENDS-FULL', { n: 100 }), 'error'); else app.handleError(e);
    } finally { setBusy(false); }
  };
  const send = async (target: 'name' | 'garage') => {
    setBusy(true);
    try {
      await app.call('reportPlayer', { body: { target, publicId: p.publicId, reason: 'offensive' } });
      setReport(false);
      showToast(tr('CL-VISIT-REPORTED'), 'success');
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code === 'DAILY_LIMIT') showToast(tr('CL-VISIT-REPORT-LIMIT'), 'error'); else app.handleError(e);
    } finally { setBusy(false); }
  };
  const label = st === 'friends' ? 'CL-VISIT-FRIENDS' : st === 'outgoing' ? 'CL-VISIT-FRIEND-SENT' : st === 'incoming' ? 'CL-FRIENDS-ACCEPT' : 'PROF-ADD-FRIEND';
  return (
    <div class="col" style={{ gap: 6 }} data-testid="visit-social">
      <div class="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button class="btn small outline" data-testid="visit-friend" disabled={off || st === 'friends' || st === 'outgoing'} onClick={friend}>{tr(label)}</button>
        <button class="btn small outline" data-testid="visit-report" disabled={off} onClick={() => setReport(!report)}>{tr('COOP-CHAT-REPORT')}</button>
      </div>
      {report ? (
        <div class="row" style={{ gap: 8, flexWrap: 'wrap' }} data-testid="visit-report-pick">
          <span class="small muted">{tr('CL-VISIT-REPORT-WHAT')}</span>
          <button class="chip" disabled={off} data-testid="report-name" onClick={() => send('name')}>{tr('CL-VISIT-REPORT-NAME')}</button>
          <button class="chip" disabled={off} data-testid="report-garage" onClick={() => send('garage')}>{tr('CL-VISIT-REPORT-GARAGE')}</button>
        </div>
      ) : null}
    </div>
  );
}
